import React, { useState, useEffect, useRef } from 'react';
import { Icon } from '@iconify/react';
import { Calendar, X, Info } from 'lucide-react';
import { getSession } from '../../../getSession';
import API_ENDPOINTS from '../../../config/apiEndpoints';
import Loader from '../../../components/Loader/Loader';
import Alert from '../../../common/Alerts/Alert';
import './InternationalSMSForm.css';

const InternationalSMSForm = ({ 
  selectedEnquiries = [], 
  isAllSelectedAcrossPages = false, 
  totalRecord = 0,
  onClose, 
  onSuccess 
}) => {
  const { userId, TokenId } = getSession();
  const [placeholders, setPlaceholders] = useState([]);
  const [showPlaceholders, setShowPlaceholders] = useState(false);
  const phRef = useRef(null);

  const getCurrentDateTimeLocal = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset() * 60000;
    return new Date(now - offset).toISOString().slice(0, 16);
  };

  const [formData, setFormData] = useState({
    senderId: '',
    message: '',
    isUnicode: false,
    urlTrack: false,
    trackedUrl: '',
    isScheduled: false,
    scheduleDateTime: getCurrentDateTimeLocal()
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [alertConfig, setAlertConfig] = useState({ show: false, type: 'success', title: '', message: '', onConfirm: null });

  useEffect(() => {
    fetchPlaceholders();
    const onDocClick = (e) => {
      if (phRef.current && !phRef.current.contains(e.target)) setShowPlaceholders(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const fetchPlaceholders = async () => {
    try {
      const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
      const response = await fetch(`${NEWV3_BASE_URL}${API_ENDPOINTS.COMMON.FUNC_TO_GET_PLACEHOLDER}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          Token: "-2295521862261168",
          Details: {
            strEntityName: "Appointment",
            strParentID: "32222"
          }
        })
      });
      const data = await response.json();
      const list = data?.d || data || [];
      if (Array.isArray(list)) {
        setPlaceholders(list.map(p => ({ key: p.Code, label: p.Text })));
      }
    } catch (err) {
      console.warn('Failed to fetch placeholders:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'message') {
        newData.isUnicode = /[^\u0000-\u007f]/.test(value);
      }
      return newData;
    });
  };

  const insertPlaceholder = (ph) => {
    const textarea = document.getElementById('sms-compose-area');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentMsg = formData.message;
    const newMsg = currentMsg.substring(0, start) + `#${ph}#` + currentMsg.substring(end);
    setFormData(prev => ({ ...prev, message: newMsg }));
    setShowPlaceholders(false);
    // Return focus to textarea
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + ph.length + 2, start + ph.length + 2);
    }, 0);
  };

  const validateSenderId = (id) => {
    const regex = /^[a-zA-Z0-9]{8,11}$/;
    return regex.test(id);
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      const d = date.getDate().toString().padStart(2, '0');
      const m = date.toLocaleString('en-US', { month: 'short' });
      const y = date.getFullYear();
      const hh = date.getHours().toString().padStart(2, '0');
      const mm = date.getMinutes().toString().padStart(2, '0');
      return `${d}-${m}-${y} ${hh}:${mm}`;
    } catch (e) {
      return dateStr;
    }
  };

  const handleSend = async () => {
    if (!validateSenderId(formData.senderId)) {
      setError('Invalid SenderID format.');
      return;
    }
    if (!formData.message.trim()) {
      setError('Message is required.');
      return;
    }
    if (formData.urlTrack && !formData.trackedUrl.trim()) {
      setError('URL is required when URL tracking is enabled.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const API_BASE = (process.env.REACT_APP_SERVICES_AZURE_BASEURL || '').replace(/\/$/, '');
      const API_URL = `${API_BASE}${API_ENDPOINTS.ENQUIRIES.MASS_INTERNATIONAL_SMS_ID_CONVERSION}`;
      
      // Map IDs
      let ids = "";
      if (isAllSelectedAcrossPages) {
        ids = "0";
      } else {
        ids = selectedEnquiries.map(enq => {
          if (typeof enq === 'object' && enq !== null) {
            return enq.EnquiryId || enq.enquiryId || enq.id;
          }
          return enq;
        }).filter(id => id).join('#');
      }

      if (!ids) {
        setError('No enquiries selected.');
        setIsLoading(false);
        return;
      }

      const payload = {
        Token: TokenId || "-2295521862261168",
        Details: JSON.stringify({
          IDS: ids,
          Userid: Number(userId),
          massbatchid: 0,
          SenderId: formData.senderId,
          Msg: formData.message,
          urltrack: formData.urlTrack,
          TrackedUrl: formData.trackedUrl,
          SmsPlaceHolder: "",
          StartDateTime: formData.isScheduled ? formData.scheduleDateTime.replace('T', ' ') + ':00' : ""
        })
      };

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const resData = await response.json();
      // The API returns data inside Details[0]
      let details = {};
      if (resData.Details && Array.isArray(resData.Details) && resData.Details.length > 0) {
        details = resData.Details[0];
      } else if (resData.d && resData.d.Details && Array.isArray(resData.d.Details)) {
        details = resData.d.Details[0];
      }

      if (response.ok && (resData.Status === 1 || details.BatchID)) {
        const batchId = details.BatchID || "N/A";
        const totalRec = details.TotalRecords || (isAllSelectedAcrossPages ? totalRecord : selectedEnquiries.length);
        const totalCost = details.TotalCostMultiplied || "0";

        setAlertConfig({
          show: true,
          type: 'success',
          title: 'Mass operation queued successfully',
          message: `Batch ID: ${batchId}\nTotal Records: ${totalRec}\nTotal Credits Deducted: ${totalCost}`,
          onConfirm: () => {
            onSuccess();
          }
        });
      } else {
        setError(resData.Message || 'Failed to send International SMS');
      }
    } catch (err) {
      setError('An error occurred while sending the message.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="intl-sms-wrapper">
      <div className="intl-sms-content">
        <div className="intl-sms-header">
          <h2>InternationalSend SMS To Mass</h2>
          {isLoading && <Loader size="small" />}
        </div>

        <div className="intl-sms-body">
          <div className="intl-note">
            <Info size={16} />
            <span>Note: Your wallet balance should be at least 1.5 times of the estimated batch cost to perform this action</span>
          </div>

          <div className="intl-input-group">
            <label>SenderID</label>
            <input 
              type="text"
              name="senderId"
              value={formData.senderId}
              onChange={handleInputChange}
              className={formData.senderId && !validateSenderId(formData.senderId) ? 'invalid' : ''}
            />
            {formData.senderId && !validateSenderId(formData.senderId) && (
              <span className="validation-error">
                Please enter a value that is either numeric or alphabetic, with a length between 8 and 11 characters.
              </span>
            )}
          </div>

          <div className="intl-input-group">
            <div className="label-row">
              <label>Compose SMS</label>
              <span className="char-count">{formData.message.length} / 2000</span>
            </div>
            <textarea 
              id="sms-compose-area"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="Message"
              maxLength={2000}
            />
          </div>

          <div className="intl-action-row">
            <button className="placeholder-btn" onClick={() => setShowPlaceholders(true)}>
              <Icon icon="mdi:card-text-outline" fontSize={20} />
              <span>Click Icon to Insert Placeholder</span>
            </button>
            <div className="unicode-status">
              IsUnicode: <span>{formData.isUnicode ? 'True' : 'False'}</span>
            </div>
          </div>

          {showPlaceholders && (
            <div className="placeholder-popup-overlay">
              <div className="placeholder-popup" ref={phRef}>
                <div className="popup-header">
                  <h3>CHOOSE PLACEHOLDER</h3>
                  <button onClick={() => setShowPlaceholders(false)}><X size={18} /></button>
                </div>
                <div className="popup-body">
                   <select 
                    className="placeholder-select" 
                    onChange={(e) => insertPlaceholder(e.target.value)}
                    defaultValue=""
                   >
                     <option value="" disabled>Nothing selected</option>
                     {placeholders.map((ph, idx) => (
                       <option key={idx} value={ph.key}>{ph.label}</option>
                     ))}
                   </select>
                </div>
              </div>
            </div>
          )}

          <div className="intl-checkbox-group mt-4">
            <label className="custom-checkbox">
              <input 
                type="checkbox" 
                name="urlTrack"
                checked={formData.urlTrack}
                onChange={handleInputChange}
              />
              <span className="checkbox-text">UrlTrack</span>
            </label>
          </div>

          {formData.urlTrack && (
            <div className="intl-input-group mt-2">
              <label>Enter URL</label>
              <input 
                type="text"
                name="trackedUrl"
                value={formData.trackedUrl}
                onChange={handleInputChange}
                placeholder="Enter URL"
              />
            </div>
          )}

          <div className="intl-checkbox-group mt-4">
            <label className="custom-checkbox">
              <input 
                type="checkbox" 
                name="isScheduled"
                checked={formData.isScheduled}
                onChange={handleInputChange}
              />
              <span className="checkbox-text">On Schedule Date And Time</span>
            </label>
          </div>

          {formData.isScheduled && (
            <div className="intl-input-group mt-4">
              <label>Schedule Date and Time *</label>
              <div className="custom-date-picker">
                <Calendar size={18} className="calendar-icon" />
                <input 
                    type="datetime-local" 
                    value={formData.scheduleDateTime}
                    onChange={(e) => setFormData(prev => ({ ...prev, scheduleDateTime: e.target.value }))}
                    className="hidden-date-input"
                />
                <span className="date-display-text">
                    {formatDateDisplay(formData.scheduleDateTime) || 'dd-MMM-yyyy HH:mm'}
                </span>
              </div>
            </div>
          )}

          {error && <div className="error-alert">{error}</div>}
        </div>

        <div className="intl-sms-footer">
          <button className="send-sms-btn" onClick={handleSend} disabled={isLoading}>
            Send SMS
          </button>
        </div>
      </div>

      {alertConfig.show && (
        <Alert
          show={true}
          type={alertConfig.type}
          title={alertConfig.title}
          message={alertConfig.message}
          onConfirm={alertConfig.onConfirm}
          onCancel={() => setAlertConfig(prev => ({ ...prev, show: false }))}
        />
      )}
    </div>
  );
};

export default InternationalSMSForm;
